"use client";

import type { VariantProps } from "class-variance-authority";
import {
  type ChangeEvent,
  type ClipboardEvent,
  type FocusEvent,
  type InputEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useEffect,
  useRef,
} from "react";

import { cn } from "../../utils";
import { useOTPRootContext } from "./context";
import { inputVariants } from "./style";

export type OTPInputProps = VariantProps<typeof inputVariants> & InputHTMLAttributes<HTMLInputElement>;

const Input = ({ className, ...props }: OTPInputProps) => {
  const ref = useRef<HTMLInputElement | null>(null);

  const { inputRefs, size, type, isInvalid, value, setActiveInput, activeInput, onChange, disabled } =
    useOTPRootContext();

  useEffect(() => {
    inputRefs.current.push(ref.current);
  }, []);

  const isInputNum = type === "number" || type === "tel";

  const getOTPValue = () => {
    return value ? value.toString().split("") : [];
  };

  const isInputValueValid = (value: string) => {
    const isTypeValid = isInputNum ? !isNaN(Number(value)) : typeof value === "string";
    return isTypeValid && value.trim().length === 1;
  };

  const focusInput = (index: number) => {
    const numInputs = inputRefs.current.length;
    const activeInput = Math.max(Math.min(numInputs - 1, index), 0);
    const activeInputElement = inputRefs.current[activeInput];

    if (activeInputElement) {
      activeInputElement?.focus();
      activeInputElement?.select();
      setActiveInput(activeInput);
    }
  };

  const changeCodeAtFocus = (value: string) => {
    const otp = getOTPValue();
    otp[activeInput] = value[0] || "";
    handleOTPChange(otp);
  };

  const handleOTPChange = (otp: Array<string>) => {
    const otpValue = otp.join("");
    onChange(otpValue);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (isInputValueValid(value)) {
      changeCodeAtFocus(value);
      focusInput(activeInput + 1);
    }
  };

  const handleInput = (event: InputEvent<HTMLInputElement>) => {
    const { nativeEvent, currentTarget } = event;
    const value = currentTarget.value;
    const numInputs = inputRefs.current.length;

    if (!isInputValueValid(value)) {
      // Pasting from the native autofill suggestion on a mobile device can pass
      // the pasted string as one long input to one of the cells. This ensures
      // that we handle the full input and not just the first character.
      if (value.length === numInputs) {
        const hasInvalidInput = value.split("").some(cellInput => !isInputValueValid(cellInput));
        if (!hasInvalidInput) {
          handleOTPChange(value.split(""));
          focusInput(numInputs - 1);
        }
      }

      // This was added previously to handle and edge case for dealing with
      // keyCode "229 Unidentified" on Android. Check if this is still needed.
      if (nativeEvent.data === null && nativeEvent.inputType === "deleteContentBackward") {
        event.preventDefault();
        changeCodeAtFocus("");
        focusInput(activeInput - 1);
      }

      // Clear the input if it's not valid value because firefox allows
      // pasting non-numeric characters in a number type input
      currentTarget.value = "";
    }
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => (index: number) => {
    setActiveInput(index);
    event.target.select();
  };

  const handleBlur = () => {
    setActiveInput(activeInput - 1);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const numInputs = inputRefs.current.length;
    const otp = getOTPValue();
    let nextActiveInput = activeInput;

    // Get pastedData in an array of max size (num of inputs - current position)
    const pastedData = event.clipboardData
      .getData("text/plain")
      .slice(0, numInputs - activeInput)
      .split("");

    // Prevent pasting if the clipboard data contains non-numeric values for number inputs
    if (isInputNum && pastedData.some(value => isNaN(Number(value)))) {
      return;
    }

    // Paste data from focused input onwards
    for (let pos = 0; pos < numInputs; ++pos) {
      if (pos >= activeInput && pastedData.length > 0) {
        otp[pos] = pastedData.shift() ?? "";
        nextActiveInput++;
      }
    }

    focusInput(nextActiveInput);
    handleOTPChange(otp);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const otp = getOTPValue();
    if ([event.code, event.key].includes("Backspace")) {
      event.preventDefault();
      changeCodeAtFocus("");
      focusInput(activeInput - 1);
    } else if (event.code === "Delete") {
      event.preventDefault();
      changeCodeAtFocus("");
    } else if (event.code === "ArrowLeft") {
      event.preventDefault();
      focusInput(activeInput - 1);
    } else if (event.code === "ArrowRight") {
      event.preventDefault();
      focusInput(activeInput + 1);
    }
    // React does not trigger onChange when the same value is entered
    // again. So we need to focus the next input manually in this case.
    // eslint-disable-next-line sonarjs/no-duplicated-branches
    else if (event.key === otp[activeInput]) {
      event.preventDefault();
      focusInput(activeInput + 1);
    } else if (
      event.code === "Spacebar" ||
      event.code === "Space" ||
      event.code === "ArrowUp" ||
      event.code === "ArrowDown"
    ) {
      event.preventDefault();
    }
  };

  const inputValue =
    getOTPValue()[inputRefs.current.findIndex((input: HTMLInputElement | null) => input === ref.current)] ?? "";

  return (
    <input
      {...props}
      className={cn(
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
        inputVariants({ size }),
        inputValue && "border-primary-80",
        isInvalid && "border-error-80! focus:shadow-input-error",
        className
      )}
      ref={ref}
      onBlur={handleBlur}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={handleChange}
      onFocus={event => handleFocus(event)(inputRefs.current.findIndex(input => input === ref.current))}
      autoComplete="off"
      aria-label={`Please enter OTP character ${inputRefs.current.findIndex(input => input === ref.current) + 1}`}
      value={inputValue}
      inputMode={isInputNum ? "numeric" : "text"}
      type={type}
      disabled={disabled}
    />
  );
};

export default Input;
